<script setup lang="ts">
import type { User } from '@/types'
import { Form, Head, usePage } from '@inertiajs/vue3'

import { firstFormError } from '@/lib/forms'
import { dashboardLayout } from '@/lib/layouts'
import { adminUserPath, adminUsersPath } from '@/routes'
import { permissionLevels } from '@/types/permissions'

defineOptions({
  layout: dashboardLayout,
})

const page = usePage()
const user = page.props.user as User
</script>

<template>
  <Head :title="`Edit ${user.name}`" />

  <UDashboardPanel id="admin-users-edit">
    <template #header>
      <UDashboardNavbar :title="`Edit ${user.name}`" :ui="{ right: 'gap-3' }">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="mx-auto w-full max-w-2xl">
        <Form
          v-slot="{ errors, processing }"
          method="patch"
          :action="adminUserPath(user.id)"
          disable-while-processing
          class="flex flex-col gap-6"
        >
          <div class="grid gap-6">
            <UFormField
              label="Name"
              name="name"
              :error="firstFormError(errors.name)"
              required
            >
              <UInput
                id="name"
                name="name"
                type="text"
                :default-value="user.name"
                required
                autofocus
                :tabindex="1"
                autocomplete="name"
                placeholder="Full name"
                icon="i-lucide-user"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Email address"
              name="email"
              :error="firstFormError(errors.email)"
              required
            >
              <UInput
                id="email"
                name="email"
                type="email"
                :default-value="user.email"
                required
                :tabindex="2"
                autocomplete="email"
                placeholder="email@example.com"
                icon="i-lucide-mail"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Permission level"
              name="permission_level"
              :error="firstFormError(errors.permission_level)"
            >
              <USelect
                id="permission_level"
                name="permission_level"
                :items="permissionLevels"
                :default-value="user.permission_level"
                :tabindex="3"
                placeholder="Select permission level"
                icon="i-lucide-shield"
                class="w-full"
              />
            </UFormField>

            <div class="flex gap-2">
              <UButton
                type="submit"
                class="mt-2"
                :tabindex="4"
                :loading="processing"
                label="Save changes"
                icon="i-lucide-save"
              />
              <UButton
                color="neutral"
                variant="ghost"
                class="mt-2"
                label="Cancel"
                :to="adminUsersPath()"
              />
            </div>
          </div>
        </Form>
      </div>
    </template>
  </UDashboardPanel>
</template>
